import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

const schema = yup.object().shape({
  first_name: yup.string().required("First name is required"),
  username: yup
    .string()
    .required("Username is required")
    .matches(/^\S*$/, "Username cannot contain spaces"),
  department_id: yup.string().required("Department is required"),
  branch_id: yup.string().required("Branch is required"),
  role_id: yup.string().required("Designation is required"),
  shift_id: yup.string().required("Shift is required"),
  status: yup.string().required("Status is required"),
});

const EmployeeFormModal = ({
  onClose,
  show,
  isEditing,
  getInitials,
  imageChange,
  isLoading,
  onMouseEnter,
  onMouseLeave,
  onSubmit,
  previewUrl,
  fileInputKey,
  onHover,
  shifts,
  departments,
  branches,
  Spinner,
  loader,
  currentEmployee,
  roles,
  defaultValues = {},
}) => {
  const [activeTab, setActiveTab] = useState("required");

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      ...defaultValues,
      enable_notification: isEditing
        ? currentEmployee?.enable_notification === 1
        : true,
      auto_approve: isEditing ? currentEmployee?.auto_approve === 1 : true,
    },
  });

  useEffect(() => {
    if (isEditing && currentEmployee) {
      reset({
        first_name: currentEmployee.first_name || "",
        last_name: currentEmployee.last_name || "",
        username: currentEmployee.username || "",
        sequence_number: currentEmployee.sequence_number || "",
        email: currentEmployee.email || "",
        phone_number: currentEmployee.phone_number || "",
        department_id: currentEmployee.department_id || "",
        branch_id: currentEmployee.branch_id || "",
        role_id: currentEmployee.role_id || "",
        shift_id: currentEmployee.shift_id || "",
        monthly_salary: currentEmployee.monthly_salary || "",
        password: "",
        fingerprint_id: currentEmployee.fingerprint_id || "",
        enable_notification: currentEmployee.enable_notification === 1,
        auto_approve: currentEmployee.auto_approve === 1,
        status: currentEmployee.status || "active",
      });
    }
  }, [isEditing, currentEmployee, reset]);

  const ProfileSection = () => (
  <div className="row g-3">
    <div className="col-sm-12 col-md-3">
      <div className="d-flex justify-content-center align-items-center position-relative mb-2">
        <div
          className="profileImage"
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        >
          {previewUrl ? (
            <img
              src={previewUrl}
              alt="Profile"
              className="image"
            />
          ) : (
            <span className="profileText">
              {getInitials(currentEmployee?.first_name || "")}
            </span>
          )}

          {onHover && (
            <div
              className="edit-icon"
              style={{ cursor: "pointer" }}
              onClick={() =>
                document.getElementById("imageInput").click()
              }
            >
              <i className="bi bi-pen"></i>
            </div>
          )}
        </div>
        <input
          id="imageInput"
          type="file"
          accept="image/*"
          className="form-control d-none"
          onChange={imageChange}
        />
      </div>
    </div>
    <div className="col-sm-12 col-md-9">
      <div className="row g-3">
        <div className="col-md-6">
            <label className="form-label fw-semibold">
              First Name
            </label>
            <input
              type="text"
              className={`form-control ${errors.first_name ? "is-invalid" : ""}`}
              {...register("first_name")}
              placeholder="Enter First Name"
            />
            {errors.first_name && (
              <div className="invalid-feedback d-flex align-items-center">
                {errors.first_name.message}
              </div>
            )}
        </div>
        
        <div className="col-md-6">
            <label className="form-label fw-semibold">Last Name</label>
            <input
              type="text"
              className="form-control"
              {...register("last_name")}
              placeholder="Enter Last Name"
            />
        </div>
        
        <div className="col-md-6">
            <label className="form-label fw-semibold">
              Username
            </label>
            <input
              type="text"
              className={`form-control ${errors.username ? "is-invalid" : ""}`}
              {...register("username")}
              placeholder="Enter Username"
            />
            {errors.username && (
              <div className="invalid-feedback d-flex align-items-center">
                {errors.username.message}
              </div>
            )}
        </div>
        
        <div className="col-md-6">
            <label className="form-label fw-semibold">
              Employee Device ID
            </label>
            <input
              type="text"
              className="form-control"
              {...register("sequence_number")}
              placeholder="Enter ID"
            />
        </div>
      </div>
    </div>
  </div>
);

  const RequiredInfoTab = () => (
    <div>
      <ProfileSection />
      <div className="row g-3 mt-3">
        <div className="col-md-6">
            <label className="form-label fw-semibold">
              Designation
            </label>
            <select
              className={`form-select ${errors.role_id ? "is-invalid" : ""}`}
              {...register("role_id")}
            >
              <option value="">Select Designation</option>
              {roles.map((role) => (
                <option key={role.role_id} value={role.role_id}>
                  {role.role_name}
                </option>
              ))}
            </select>
            {errors.role_id && (
              <div className="invalid-feedback d-flex align-items-center">
                {errors.role_id.message}
              </div>
            )}
        </div>

        <div className="col-md-6">
            <label className="form-label fw-semibold">
              Department
            </label>
            <select
              className={`form-select ${errors.department_id ? "is-invalid" : ""}`}
              {...register("department_id")}
            >
              <option value="">Select Department</option>
              {departments.map((dept) => (
                <option key={dept.department_id} value={dept.department_id}>
                  {dept.department_name}
                </option>
              ))}
            </select>
            {errors.department_id && (
              <div className="invalid-feedback d-flex align-items-center">
                {errors.department_id.message}
              </div>
            )}
        </div>

        <div className="col-md-6">
            <label className="form-label fw-semibold">
              Branch
            </label>
            <select
              className={`form-select ${errors.branch_id ? "is-invalid" : ""}`}
              {...register("branch_id")}
            >
              <option value="">Select Branch</option>
              {branches?.map((branch) => (
                <option key={branch.branch_id} value={branch.branch_id}>
                  {branch.name}
                </option>
              ))}
            </select>
            {errors.branch_id && (
              <div className="invalid-feedback d-flex align-items-center">
                {errors.branch_id.message}
              </div>
            )}
        </div>

        <div className="col-md-6">
            <label className="form-label fw-semibold">
              Shift
            </label>
            <select
              className={`form-select ${errors.shift_id ? "is-invalid" : ""}`}
              {...register("shift_id")}
            >
              <option value="">Select Shift</option>
              {shifts.map((shift) => (
                <option key={shift?.id} value={shift?.id}>
                  {shift?.shift_name}
                </option>
              ))}
            </select>
            {errors.shift_id && (
              <div className="invalid-feedback d-flex align-items-center">
                {errors.shift_id.message}
              </div>
            )}
        </div>

        <div className="col-md-6">
            <label className="form-label fw-semibold">
              Status
            </label>
            <select
              className={`form-select ${errors.status ? "is-invalid" : ""}`}
              {...register("status")}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            {errors.status && (
              <div className="invalid-feedback d-flex align-items-center">
                {errors.status.message}
              </div>
            )}
        </div>
      </div>
    </div>
  );

  const AdditionalInfoTab = () => (
    <div>
      <div className="row g-3">
        <div className="col-md-6">
          <label className="form-label">Email</label>
          <input
            type="email"
            className={`form-control`}
            {...register("email")}
          />
        </div>
        <div className="col-md-6">
          <label className="form-label">Phone Number</label>
          <input
            type="text"
            className={`form-control ${errors.phone_number ? "is-invalid" : ""
              }`}
            {...register("phone_number")}
          />
          {errors.phone_number && (
            <div className="invalid-feedback">
              {errors.phone_number.message}
            </div>
          )}
        </div>

        <div className="col-md-6">
          <label className="form-label">Salary</label>
          <input
            type="text"
            className="form-control"
            {...register("monthly_salary")}
          />
        </div>
        <div className="col-md-6">
          <label className="form-label">Fingerprint ID</label>
          <input
            type="text"
            className="form-control"
            {...register("fingerprint_id")}
            readOnly={isEditing}
          />
        </div>

        <div className="col-md-6">
          <label className="form-label">Password</label>
          <input
            type="password"
            className="form-control"
            {...register("password")}
          />
        </div>

        <div className="col-12 mt-5 mb-2">
          <div className="card border-0 mb-0 shadow-none bg-light">
            <div className="card-body">
              <h6 className="card-title text-dark fw-semibold mb-3 d-flex align-items-center">
                <i className="bi bi-gear me-2"></i>
                Employee Settings
              </h6>
              <div className="row g-3">
                <div className="col-md-6">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="enable_notification"
                      {...register("enable_notification")}
                      onChange={(e) =>
                        setValue("enable_notification", e.target.checked ? 1 : 0)
                      }
                      defaultChecked={!isEditing}
                    />
                    <label className="form-check-label fw-medium d-flex align-items-center" htmlFor="enable_notification">
                      Enable Notifications
                    </label>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="auto_approve"
                      {...register("auto_approve")}
                      onChange={(e) =>
                        setValue("auto_approve", e.target.checked ? 1 : 0)
                      }
                      defaultChecked={!isEditing}
                    />
                    <label className="form-check-label fw-medium d-flex align-items-center" htmlFor="auto_approve">
                      Auto Approval
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div >
  );

  return (
    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
      <div className="modal-dialog modal-lg modal-dialog-centered" style={{maxHeight: "80vh"}}>
        <div className="modal-content border-0 shadow">
          <div className="modal-header bg-light border-0">
            <h5 className="modal-title">
              {isEditing ? "Edit Employee" : "Add Employee"}
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
            ></button>
          </div>

          <div className="modal-body p-0">
            {loader ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-2 text-muted">Loading employee data...</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)}>
                {/* Tabs Navigation */}
                <div className="border-bottom">
                  <div className="nav nav-pills px-3">
                    <div className="nav-item">
                      <button
                        type="button"
                        className={`nav-link rounded-0 border-0 px-4 py-3 ${activeTab === "required" ? "active bg-light text-dark fw-semibold" : "text-muted bg-transparent"}`}
                        onClick={() => setActiveTab("required")}
                      >
                        <i className="bi bi-person-vcard me-2"></i>
                        Required Information
                      </button>
                    </div>
                    <div className="nav-item">
                      <button
                        type="button"
                        className={`nav-link rounded-0 border-0 px-4 py-3 ${activeTab === "additional" ? "active bg-light text-dark fw-semibold" : "text-muted bg-transparent"}`}
                        onClick={() => setActiveTab("additional")}
                      >
                        <i className="bi bi-info-circle me-2"></i>
                        Additional Information
                      </button>
                    </div>
                  </div>
                </div>

                {/* Tabs Content */}
                <div className="p-4" style={{ maxHeight: '62vh', overflowY: 'auto' }}>
                  {activeTab === "required" && <RequiredInfoTab />}
                  {activeTab === "additional" && <AdditionalInfoTab />}
                </div>

                {/* Form Actions */}
                <div className="modal-footer bg-light justify-content-between border-top">
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={onClose}
                  >
                    Close
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        {isEditing ? "Updating..." : "Creating..."}
                      </>
                    ) : (
                      <>
                        {isEditing ? "Update Employee" : "Create Employee"}
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeFormModal;